/**
 * chatFlowApi.ts
 *
 * Calls the backend chat-flow endpoints so the employee UI can show the
 * document list the admin attached to their invitation (invitation_various_copies).
 *
 * Backend: GET /api/chat-flow?email=...&locale=ja|en|vi
 * Resolves the latest invitation for that email and returns ordered documents + system messages.
 */
import { API_BASE_URL } from '../config';

/** One document row as returned by the backend (localized name/description + status). */
export interface ChatFlowDocumentItem {
  docCode: string;
  name: string;
  /** Japanese name for bilingual display when locale is en/vi. */
  nameJa?: string;
  description: string;
  /** Japanese description; show when locale is en/vi but description is empty. */
  descriptionJa?: string;
  status: string;
  filePath: string | null;
  /** Back-side path when uploaded separately. */
  filePathBack?: string | null;
  uploadedAt: string | null;
  /** Card-style doc: employee may upload combined file or front+back separately. */
  requiresBothSides?: boolean;
  /** Admin approval: null = not reviewed, true = approved, false = rejected */
  adminApproved?: boolean | null;
  /** Admin comment (e.g. resubmit reason) */
  adminComment?: string | null;

  /** Uploaded file rounds for this document slot (multiple rows after resubmission). */
  filesByRound?: ChatFlowDocumentFileRound[];
  /** False when the invitation includes this document but the employee has not saved a choice. */
  choiceRecorded?: boolean;
}

/** One uploaded round (submission_round) for a specific docCode. */
export interface ChatFlowDocumentFileRound {
  submissionRound: number;
  filePath: string | null;
  filePathBack?: string | null;
  uploadedAt: string | null;
  adminApproved?: boolean | null;
  adminComment?: string | null;
}

/** Full payload: invitation id, locale, message map, and ordered document list. */
export interface ChatFlowResponse {
  invitationId: string;
  /** Submission deadline as yyyy/MM/dd; empty when unset */
  deadline?: string;
  locale: string;
  /** message_key -> text already resolved for the requested locale */
  messages: Record<string, string>;
  documents: ChatFlowDocumentItem[];
  /** Ordered fill-in forms (記入書類) for this invitation */
  formsToFill?: ChatFlowFormToFillItem[];
  /** Rows in invitation_forms_to_fill for this invitation */
  formsToFillTotal?: number;
  /** empdoc_forms_to_fill rows with status completed for required form codes */
  formsToFillCompleted?: number;
  /** 基本情報 / 家族情報 admin review rows */
  profileSections?: ChatFlowProfileSectionItem[];
  /** Career-up routing: vc008_pending | has_card | no_card */
  careerUpPath?: CareerUpPathValue | null;
  /** Unread in-app notices after admin edits invitation */
  pendingNotices?: ChatFlowPendingNotice[];
}

export interface ChatFlowPendingNotice {
  id: string;
  title: string;
  body: string;
  createdAt: string;
}

export type CareerUpPathValue =
  | 'vc008_not_required'
  | 'vc008_pending'
  | 'has_card'
  | 'no_card';

/** Profile checklist section (基本情報 / 家族情報). */
export interface ChatFlowProfileSectionItem {
  section: 'general_info' | 'family_info' | string;
  name: string;
  nameJa?: string;
  status: string;
  adminApproved?: boolean | null;
  adminComment?: string | null;
}

/** One submission round for a fill-in form. */
export interface ChatFlowFormFillRound {
  submissionRound: number;
  status: string;
  completedAt: string | null;
  adminApproved?: boolean | null;
  adminComment?: string | null;
  adminCorrectedBy?: string | null;
  adminCorrectedAt?: string | null;
}

/** One fill-in form row from chat-flow. */
export interface ChatFlowFormToFillItem {
  formCode: string;
  name: string;
  nameJa?: string;
  status?: string;
  adminApproved?: boolean | null;
  adminComment?: string | null;
  roundsBySubmission?: ChatFlowFormFillRound[];
  /** False = hidden (inactive career-up form after VC008 disposition). */
  applicable?: boolean | null;
  careerUpRole?: 'confirmation' | 'application' | null;
  /** False when the form was added to the invitation and the employee has not started it. */
  choiceRecorded?: boolean;
}

/** Status values for POST /api/chat-flow/document-choice */
export type ChatFlowDocumentChoiceStatus =
  | 'send_later'
  | 'bring_when_boarding'
  | 'notyet'
  | 'skipped';

/**
 * Save employee's choice for one document (empdoc_various_copies).
 * Backend upserts: one row per (email, invitationId, docCode). notyet = "submit this time" (upload later).
 */
export async function postChatFlowDocumentChoice(
  email: string,
  invitationId: string,
  docCode: string,
  status: ChatFlowDocumentChoiceStatus
): Promise<boolean> {
  if (!API_BASE_URL || !email?.trim() || !invitationId?.trim() || !docCode?.trim()) {
    return false;
  }
  try {
    const res = await fetch(`${API_BASE_URL}/api/chat-flow/document-choice`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: email.trim(),
        invitationId: invitationId.trim(),
        docCode: docCode.trim(),
        status,
      }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * Fetches the chat flow for the logged-in employee.
 * Uses email so the server can look up invitation_recipient → invitation → invitation_various_copies.
 *
 * @param email   Must match the address the admin invited (same as EmpAccount.emailaddress).
 * @param locale  ja | en | vi — must match LanguageContext so bubbles use the same language as the rest of the app.
 * @returns       Parsed JSON, or null if 404 (no invitation / not a recipient) or network error.
 */
/** When upload returns 400, backend may send { error: string }. */
export interface UploadErrorResponse {
  error?: string;
}

/**
 * Upload a document file for the current document (submit-this-time flow).
 * Backend saves the file and updates empdoc_various_copies (status=submitted, file_path, uploaded_at).
 * On failure returns { ok: false, error?: string } so the UI can show the reason.
 */
export type VariousCopyUploadMode = 'combined' | 'separate' | 'camera';

export async function postChatFlowDocumentUpload(
  email: string,
  invitationId: string,
  docCode: string,
  file: File,
  options?: { fileBack?: File; uploadMode?: VariousCopyUploadMode }
): Promise<{ ok: true } | { ok: false; error?: string }> {
  if (!API_BASE_URL || !email?.trim() || !invitationId?.trim() || !docCode?.trim() || !file) {
    return { ok: false, error: 'missing_params' };
  }
  const form = new FormData();
  form.append('email', email.trim());
  form.append('invitationId', invitationId.trim());
  form.append('docCode', docCode.trim());
  form.append('file', file);
  if (options?.fileBack) {
    form.append('fileBack', options.fileBack);
  }
  if (options?.uploadMode) {
    form.append('uploadMode', options.uploadMode);
  }
  try {
    const res = await fetch(`${API_BASE_URL}/api/chat-flow/documents/upload`, {
      method: 'POST',
      body: form,
    });
    if (res.ok) return { ok: true };
    let error: string | undefined;
    try {
      const body = (await res.json()) as UploadErrorResponse;
      error = body?.error;
    } catch {
      error = res.status === 400 ? 'bad_request' : 'upload_failed';
    }
    return { ok: false, error };
  } catch {
    return { ok: false, error: 'network_error' };
  }
}

export async function fetchChatFlow(
  email: string,
  locale: string,
  invitationId?: string | null
): Promise<ChatFlowResponse | null> {
  if (!API_BASE_URL || !email || !email.trim()) {
    return null;
  }
  const params = new URLSearchParams({
    email: email.trim(),
    locale: locale || 'ja',
  });
  if (invitationId?.trim()) {
    params.set('invitationId', invitationId.trim());
  }
  try {
    const res = await fetch(`${API_BASE_URL}/api/chat-flow?${params.toString()}`);
    if (!res.ok) {
      // 404 = no recipient row or no invitation found for this email
      return null;
    }
    return (await res.json()) as ChatFlowResponse;
  } catch {
    return null;
  }
}

export async function fetchEmployeeAccountStatus(email: string): Promise<string | null> {
  if (!API_BASE_URL || !email?.trim()) return null;
  const params = new URLSearchParams({ email: email.trim() });
  try {
    const res = await fetch(`${API_BASE_URL}/api/chat-flow/account-status?${params.toString()}`);
    if (!res.ok) return null;
    const data = (await res.json()) as { status?: string };
    return typeof data?.status === 'string' ? data.status : null;
  } catch {
    return null;
  }
}

/**
 * Register that the employee started a fill-in form (creates {@code empdoc_forms_to_fill} on the server).
 * Call when the user taps Start on a checklist form row (after general information is ready).
 */
export async function postStartFormToFill(
  email: string,
  invitationId: string,
  formCode: string
): Promise<boolean> {
  if (!API_BASE_URL || !email?.trim() || !invitationId?.trim() || !formCode?.trim()) {
    return false;
  }
  try {
    const res = await fetch(`${API_BASE_URL}/api/chat-flow/forms-to-fill/start`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: email.trim(),
        invitationId: invitationId.trim(),
        formCode: formCode.trim(),
      }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * Mark a fill-in form as completed on the server ({@code empdoc_forms_to_fill.status = completed}),
 * e.g. after the employee confirms the PDF preview.
 */
export async function postCompleteFormToFill(
  email: string,
  invitationId: string,
  formCode: string
): Promise<boolean> {
  if (!API_BASE_URL || !email?.trim() || !invitationId?.trim() || !formCode?.trim()) {
    return false;
  }
  try {
    const res = await fetch(`${API_BASE_URL}/api/chat-flow/forms-to-fill/complete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: email.trim(),
        invitationId: invitationId.trim(),
        formCode: formCode.trim(),
      }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * Set a fill-in form back to in progress after completion (employee tapped Edit on the checklist).
 */
export async function postReopenFormToFill(
  email: string,
  invitationId: string,
  formCode: string
): Promise<boolean> {
  if (!API_BASE_URL || !email?.trim() || !invitationId?.trim() || !formCode?.trim()) {
    return false;
  }
  try {
    const res = await fetch(`${API_BASE_URL}/api/chat-flow/forms-to-fill/reopen`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: email.trim(),
        invitationId: invitationId.trim(),
        formCode: formCode.trim(),
      }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/** Admin review for 基本情報 / 家族情報 on emp_profile. */
export async function putProfileSectionReview(
  email: string,
  invitationId: string,
  section: 'general_info' | 'family_info',
  adminApproved: boolean,
  adminComment: string | null
): Promise<boolean> {
  if (!API_BASE_URL || !email?.trim() || !invitationId?.trim() || !section) {
    return false;
  }
  try {
    const res = await fetch(`${API_BASE_URL}/api/chat-flow/profile-sections/review`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: email.trim(),
        invitationId: invitationId.trim(),
        section,
        adminApproved,
        adminComment,
      }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * Mark the employee account as draft when they open various copies for the first time (status was "sent").
 */
export async function postMarkDraft(email: string): Promise<boolean> {
  if (!API_BASE_URL || !email?.trim()) {
    return false;
  }
  try {
    const res = await fetch(`${API_BASE_URL}/api/chat-flow/mark-draft`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email.trim() }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * Mark the invitation recipient as submitted (status=submitted, submission_round=0).
 * Called when the employee clicks Submit on the document submission checklist.
 */
export async function postMarkSubmitted(
  email: string,
  invitationId: string
): Promise<boolean> {
  if (!API_BASE_URL || !email?.trim() || !invitationId?.trim()) {
    return false;
  }
  try {
    const res = await fetch(`${API_BASE_URL}/api/chat-flow/mark-submitted`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: email.trim(),
        invitationId: invitationId.trim(),
      }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * Acknowledge an in-app invitation update notice (employee popup dismiss).
 */
export async function postAckChatFlowNotice(
  noticeId: string,
  email: string
): Promise<boolean> {
  if (!API_BASE_URL || !noticeId?.trim() || !email?.trim()) {
    return false;
  }
  const params = new URLSearchParams({ email: email.trim() });
  try {
    const res = await fetch(
      `${API_BASE_URL}/api/chat-flow/notices/${encodeURIComponent(noticeId.trim())}/ack?${params.toString()}`,
      { method: 'POST' }
    );
    return res.ok;
  } catch {
    return false;
  }
}

